import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CreateClientDto } from '../services/clients.service';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/axios';
import { companiesService } from '@/features/companies/services/companies.service';

interface NotionBoard {
  id: string;
  name: string;
  description?: string | null;
}

interface User {
  id: string;
  email: string;
}

export function ClientForm({
  initialData,
  onSubmit,
  onCancel,
  isLoading,
}: {
  // a API devolve null nos campos vazios; o formulário sempre envia o DTO completo
  initialData?: { [K in Exclude<keyof CreateClientDto, 'reimbursementMonthlyBudget'>]?: CreateClientDto[K] | null } & {
    reimbursementMonthlyBudget?: number | string | null; // numeric do banco chega como string
  };
  onSubmit: (data: CreateClientDto) => void;
  onCancel?: () => void;
  isLoading?: boolean;
}) {
  const [name, setName] = useState(initialData?.name || '');
  const [tradeName, setTradeName] = useState(initialData?.tradeName || '');
  const [cnpj, setCnpj] = useState(initialData?.cnpj || '');
  const [openingDate, setOpeningDate] = useState(
    initialData?.openingDate
      ? new Date(initialData.openingDate).toISOString().split('T')[0]
      : '',
  );
  const [notionBoardId, setNotionBoardId] = useState(
    initialData?.notionBoardId ?? '',
  );
  const [accountManagerId, setAccountManagerId] = useState(
    initialData?.accountManagerId || '',
  );
  const [companyId, setCompanyId] = useState(initialData?.companyId || '');
  const [isActive, setIsActive] = useState(initialData?.isActive ?? true);
  const [clientType, setClientType] = useState<'internal' | 'external'>(
    initialData?.clientType ?? 'external',
  );
  const [reimbursementMonthlyBudget, setReimbursementMonthlyBudget] = useState(
    initialData?.reimbursementMonthlyBudget != null
      ? String(initialData.reimbursementMonthlyBudget)
      : '',
  );

  // Buscar boards do Notion disponíveis
  const { data: notionBoards = [] } = useQuery<NotionBoard[]>({
    queryKey: ['notion-boards'],
    queryFn: async () => {
      const response = await api.get('/notion/boards');
      return response.data;
    },
  });

  // Buscar usuários para gerente de conta
  const { data: users = [] } = useQuery<User[]>({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await api.get('/users');
      return response.data;
    },
  });

  const { data: companies = [] } = useQuery({
    queryKey: ['companies-list'],
    queryFn: () => companiesService.listCompanies(),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const budget =
      reimbursementMonthlyBudget === ''
        ? undefined
        : parseFloat(reimbursementMonthlyBudget);
    onSubmit({
      name,
      tradeName: tradeName || undefined,
      cnpj: cnpj || undefined,
      openingDate: openingDate || undefined,
      notionBoardId: notionBoardId === '' ? null : notionBoardId,
      accountManagerId: accountManagerId || undefined,
      companyId: companyId || undefined,
      isActive,
      reimbursementMonthlyBudget:
        budget != null && !Number.isNaN(budget) ? budget : undefined,
      clientType,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">
          Nome <span className="text-destructive">*</span>
        </Label>
        <Input
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nome do cliente"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="tradeName">Nome Fantasia</Label>
        <Input
          id="tradeName"
          value={tradeName}
          onChange={(e) => setTradeName(e.target.value)}
          placeholder="Nome fantasia"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="cnpj">CNPJ</Label>
        <Input
          id="cnpj"
          value={cnpj}
          onChange={(e) => setCnpj(e.target.value)}
          placeholder="00.000.000/0000-00"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="openingDate">Data de Abertura</Label>
        <Input
          id="openingDate"
          type="date"
          value={openingDate}
          onChange={(e) => setOpeningDate(e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="notionBoardId">Board do Notion</Label>
        <Select value={notionBoardId || 'none'} onValueChange={(value) => setNotionBoardId(value === 'none' ? '' : value)}>
          <SelectTrigger>
            <SelectValue placeholder="Selecione um board" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Nenhum</SelectItem>
            {notionBoards.map((board) => (
              <SelectItem key={board.id} value={board.id}>
                {board.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="accountManagerId">Gerente de Conta</Label>
        <Select value={accountManagerId || 'none'} onValueChange={(value) => setAccountManagerId(value === 'none' ? '' : value)}>
          <SelectTrigger>
            <SelectValue placeholder="Selecione um gerente" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Nenhum</SelectItem>
            {users.map((user) => (
              <SelectItem key={user.id} value={user.id}>
                {user.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="companyId">Empresa</Label>
        <Select
          value={companyId || 'none'}
          onValueChange={(value) => setCompanyId(value === 'none' ? '' : value)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Selecione uma empresa" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Nenhuma</SelectItem>
            {companies.map((company) => (
              <SelectItem key={company.id} value={company.id}>
                {company.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="reimbursementMonthlyBudget">
          Verba mensal para reembolsos (R$)
        </Label>
        <Input
          id="reimbursementMonthlyBudget"
          type="number"
          step="0.01"
          min="0"
          value={reimbursementMonthlyBudget}
          onChange={(e) => setReimbursementMonthlyBudget(e.target.value)}
          placeholder="Ex: 5000"
        />
        <p className="text-xs text-muted-foreground">
          Limite mensal de reembolsos para este cliente (opcional).
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="clientType">
          Categoria <span className="text-destructive">*</span>
        </Label>
        <Select
          value={clientType}
          onValueChange={(value) => setClientType(value as 'internal' | 'external')}
        >
          <SelectTrigger id="clientType">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="external">Externo</SelectItem>
            <SelectItem value="internal">Interno</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="isActive">Status</Label>
        <Select
          value={isActive ? 'true' : 'false'}
          onValueChange={(value) => setIsActive(value === 'true')}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="true">Ativo</SelectItem>
            <SelectItem value="false">Inativo</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex gap-2 justify-end">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
        )}
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>
    </form>
  );
}
